// React Hook for Geolocation and Currency Management
import { useState, useEffect, useContext, createContext } from 'react';
import { geoLocationService } from './geoLocation';

// Create context for global state management
const GeoLocationContext = createContext();

// Context Provider Component
export const GeoLocationProvider = ({ children }) => {
  const defaultLocation = geoLocationService.getDefaultLocation();

  const [userLocation, setUserLocation] = useState(defaultLocation);
  const [userCurrency, setUserCurrency] = useState(defaultLocation.currency);
  const [recommendedDataCenter, setRecommendedDataCenter] = useState('India');
  const [exchangeRates, setExchangeRates] = useState(() => geoLocationService.getFallbackRates());
  const [isLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize geolocation service
  useEffect(() => {
    let isMounted = true;

    const initializeService = async () => {
      try {
        const result = await geoLocationService.initialize();
        if (!isMounted) {
          return;
        }
        
        setUserLocation(result.location || geoLocationService.getDefaultLocation());
        setUserCurrency(result.currency || 'INR');
        setRecommendedDataCenter(result.recommendedDataCenter || 'India');
        setExchangeRates(geoLocationService.exchangeRates || geoLocationService.getFallbackRates());
        setError(null);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        console.error('Failed to initialize geolocation service:', err);
        setError(err.message);
        
        // Set defaults on error
        setUserLocation(geoLocationService.getDefaultLocation());
        setUserCurrency('INR');
        setRecommendedDataCenter('India');
      }
    };

    initializeService();

    // Refresh exchange rates every 30 minutes
    const rateRefreshInterval = setInterval(async () => {
      try {
        await geoLocationService.getExchangeRates();
        setExchangeRates(geoLocationService.exchangeRates);
      } catch (err) {
        console.error('Failed to refresh exchange rates:', err);
      }
    }, 30 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(rateRefreshInterval);
    };
  }, []);

  // Convert price function
  const convertPrice = (priceInINR, targetCurrency = userCurrency) => {
    return geoLocationService.convertPrice(priceInINR, targetCurrency);
  };

  // Format price function
  const formatPrice = (price, currency = userCurrency) => {
    return geoLocationService.formatPrice(price, currency);
  };

  // Manual currency override
  const setCurrency = (currency) => {
    setUserCurrency(currency);
  };

  // Get pricing for specific data center
  const getDataCenterPricing = (location, planType) => {
    // This would integrate with your existing pricing data
    const pricingMap = {
      'India': {
        'shared-starter': 79,
        'shared-pro': 109,
        'shared-elite': 149,
        'cpanel-start': 99,
        'cpanel-pro': 129,
        'cpanel-elite': 169,
        'vps-nvme-1': 499,
        'vps-nvme-2': 899,
        'vps-nvme-3': 1599,
        'vps-nvme-4': 2399,
        'vps-nvme-5': 3599,
        'vps-nvme-6': 4799,
        'reseller-starter-monthly': 799,
        'reseller-pro-monthly': 999,
        'reseller-premium-monthly': 1499,
        'reseller-starter-yearly': 9588,
        'reseller-pro-yearly': 11988,
        'reseller-premium-yearly': 17988
      },
      'USA': {
        'shared-starter': 89,
        'shared-pro': 119,
        'shared-elite': 159,
        'cpanel-start': 109,
        'cpanel-pro': 139,
        'cpanel-elite': 179,
        'vps-nvme-1': 449,
        'vps-nvme-2': 749,
        'vps-nvme-3': 1399,
        'vps-nvme-4': 2099,
        'vps-nvme-5': 3199,
        'vps-nvme-6': 4299,
        'reseller-starter-monthly': 679,
        'reseller-pro-monthly': 899,
        'reseller-premium-monthly': 1449,
        'reseller-starter-yearly': 8149,
        'reseller-pro-yearly': 10789,
        'reseller-premium-yearly': 17389
      },
      'Germany': {
        'shared-starter': 85,
        'shared-pro': 115,
        'shared-elite': 155,
        'cpanel-start': 105,
        'cpanel-pro': 135,
        'cpanel-elite': 175,
        'vps-nvme-1': 429,
        'vps-nvme-2': 729,
        'vps-nvme-3': 1359,
        'vps-nvme-4': 2059,
        'vps-nvme-5': 3159,
        'vps-nvme-6': 4259,
        'reseller-starter-monthly': 659,
        'reseller-pro-monthly': 879,
        'reseller-premium-monthly': 1429,
        'reseller-starter-yearly': 7909,
        'reseller-pro-yearly': 10549,
        'reseller-premium-yearly': 17149
      },
      'Singapore': {
        'shared-starter': 95,
        'shared-pro': 125,
        'shared-elite': 165,
        'cpanel-start': 115,
        'cpanel-pro': 145,
        'cpanel-elite': 185,
        'vps-nvme-1': 479,
        'vps-nvme-2': 799,
        'vps-nvme-3': 1479,
        'vps-nvme-4': 2199,
        'vps-nvme-5': 3399,
        'vps-nvme-6': 4599,
        'reseller-starter-monthly': 699,
        'reseller-pro-monthly': 919,
        'reseller-premium-monthly': 1499,
        'reseller-starter-yearly': 8389,
        'reseller-pro-yearly': 11029,
        'reseller-premium-yearly': 17989
      }
    };

    const basePriceINR = pricingMap[location]?.[planType] || pricingMap['India'][planType] || 0;
    return convertPrice(basePriceINR, userCurrency);
  };

  const contextValue = {
    userLocation,
    userCurrency,
    recommendedDataCenter,
    exchangeRates,
    isLoading,
    error,
    convertPrice,
    formatPrice,
    setCurrency,
    getDataCenterPricing,
    supportedCurrencies: geoLocationService.supportedCurrencies,
    getCurrencySymbol: geoLocationService.getCurrencySymbol.bind(geoLocationService)
  };

  return (
    <GeoLocationContext.Provider value={contextValue}>
      {children}
    </GeoLocationContext.Provider>
  );
};

// Custom hook to use the geolocation context
export const useGeoLocation = () => {
  const context = useContext(GeoLocationContext);
  if (!context) {
    throw new Error('useGeoLocation must be used within a GeoLocationProvider');
  }
  return context;
};

// Hook for pricing conversion
export const usePricing = () => {
  const {
    convertPrice,
    formatPrice,
    userCurrency,
    getDataCenterPricing,
    getCurrencySymbol
  } = useGeoLocation();

  return {
    convertPrice,
    formatPrice,
    userCurrency,
    getDataCenterPricing,
    getCurrencySymbol
  };
};

// Hook for data center recommendations
export const useDataCenter = () => {
  const {
    userLocation,
    recommendedDataCenter,
    isLoading
  } = useGeoLocation();

  return {
    userLocation,
    recommendedDataCenter,
    isLoading
  };
};
